/**
 * Media Browser Code Generator - Timeline integration through JavaScript code generation
 * 
 * This service generates JavaScript code for timeline operations initiated from the media browser,
 * providing seamless integration between media selection and timeline manipulation.
 */

import timelineStateManager from './timelineStateManager.js';
import smartCodeGenerator from './smartCodeGenerator.js';

class MediaBrowserCodeGenerator {
  constructor() {
    this.codeHistory = [];
    this.operationTemplates = new Map();
    this.contextAnalyzer = null;
    
    this._initializeOperationTemplates();
  }

  /**
   * Generate code for adding media items to timeline
   */
  generateAddToTimelineCode(mediaItems, options = {}) {
    const {
      position = 'end', // 'end', 'start', 'playhead', 'after', 'before'
      targetTime = null,
      targetClipId = null,
      arrangement = 'sequence', // 'sequence', 'overlay', 'parallel'
      spacing = 0,
      transitions = false
    } = options;

    // Handle single item vs multiple items
    const items = Array.isArray(mediaItems) ? mediaItems : [mediaItems];
    
    if (items.length === 1) {
      return this._generateSingleAddCode(items[0], position, targetTime, targetClipId);
    } else {
      return this._generateMultipleAddCode(items, arrangement, spacing, transitions, position);
    }
  }

  /**
   * Generate code for replacing clips
   */
  generateReplaceClipCode(newMediaItem, targetClipId = null, options = {}) {
    const {
      preserveTiming = true,
      adjustDuration = 'auto', // 'auto', 'stretch', 'crop', 'preserve'
      maintainPositions = true
    } = options;

    const template = this.operationTemplates.get('replaceClip');
    
    let code = `// Replace clip with ${newMediaItem.name}\n`;
    
    if (targetClipId) {
      // Replace specific clip
      code += `const targetClip = timeline.getClip('${targetClipId}');\n`;
    } else {
      // Replace selected clip(s)
      code += `const selectedClips = timeline.getSelectedClips();\n`;
      code += `if (selectedClips.length === 0) {\n`;
      code += `  console.log('No clips selected for replacement');\n`;
      code += `  return;\n`;
      code += `}\n`;
      code += `const targetClip = selectedClips[0];\n`;
    }

    code += `\nconst newClip = {\n`;
    code += `  id: '${this._generateClipId(newMediaItem)}',\n`;
    code += `  name: '${newMediaItem.name}',\n`;
    code += `  url: '${newMediaItem.url}',\n`;
    code += `  duration: ${newMediaItem.duration},\n`;
    code += `  tags: ${JSON.stringify(newMediaItem.tags || [])}\n`;
    code += `};\n\n`;

    if (preserveTiming) {
      code += `// Preserve original timing\n`;
      code += `newClip.startTime = targetClip.startTime;\n`;
      
      if (adjustDuration === 'stretch') {
        code += `newClip.duration = targetClip.duration;\n`;
      } else if (adjustDuration === 'crop') {
        code += `newClip.duration = Math.min(newClip.duration, targetClip.duration);\n`;
      }
    }

    code += `\ntimeline.replaceClip(targetClip.id, newClip);\n`;
    
    if (maintainPositions) {
      code += `timeline.maintainClipPositions();\n`;
    }

    return this._wrapWithMetadata(code, 'replace_clip', {
      mediaItem: newMediaItem,
      targetClipId,
      options
    });
  }

  /**
   * Generate code for inserting at playhead
   */
  generateInsertAtPlayheadCode(mediaItems, currentTime, options = {}) {
    const {
      splitExisting = true,
      pushClips = false,
      createGap = false,
      gapDuration = 0.5
    } = options;

    const items = Array.isArray(mediaItems) ? mediaItems : [mediaItems];
    let code = `// Insert ${items.length === 1 ? items[0].name : `${items.length} clips`} at playhead\n`;
    code += `const insertTime = ${currentTime};\n\n`;

    if (splitExisting) {
      code += `// Split existing clip at playhead if necessary\n`;
      code += `const clipsAtTime = timeline.getClipsAtTime(insertTime);\n`;
      code += `clipsAtTime.forEach(clip => {\n`;
      code += `  if (clip.startTime < insertTime && clip.startTime + clip.duration > insertTime) {\n`;
      code += `    timeline.splitClip(clip.id, insertTime);\n`;
      code += `  }\n`;
      code += `});\n\n`;
    }

    if (pushClips) {
      code += `// Push subsequent clips to make room\n`;
      const totalDuration = items.reduce((sum, item) => sum + item.duration, 0);
      code += `const totalDuration = ${totalDuration};\n`;
      code += `timeline.pushClipsAfterTime(insertTime, totalDuration);\n\n`;
    }

    // Insert clips
    let insertPosition = currentTime;
    
    if (createGap) {
      insertPosition += gapDuration;
      code += `// Add gap before insertion\n`;
      code += `let currentInsertTime = insertTime + ${gapDuration};\n\n`;
    } else {
      code += `let currentInsertTime = insertTime;\n\n`;
    }

    items.forEach((item, index) => {
      code += `// Insert ${item.name}\n`;
      code += `const clip${index} = {\n`;
      code += `  id: '${this._generateClipId(item)}',\n`;
      code += `  name: '${item.name}',\n`;
      code += `  url: '${item.url}',\n`;
      code += `  duration: ${item.duration},\n`;
      code += `  tags: ${JSON.stringify(item.tags || [])}\n`;
      code += `};\n`;
      code += `timeline.addClip(clip${index}, currentInsertTime);\n`;
      code += `currentInsertTime += ${item.duration};\n\n`;
    });

    return this._wrapWithMetadata(code, 'insert_at_playhead', {
      mediaItems: items,
      currentTime,
      options
    });
  }

  /**
   * Generate code for smart placement based on content analysis
   */
  generateSmartPlacementCode(mediaItems, timelineContext, options = {}) {
    const {
      strategy = 'auto', // 'auto', 'content_match', 'chronological', 'energy_flow'
      considerTags = true,
      maintainFlow = true,
      optimizeTransitions = false
    } = options;

    const items = Array.isArray(mediaItems) ? mediaItems : [mediaItems];
    
    // Analyze timeline context for smart placement
    const analysis = this._analyzeTimelineForPlacement(timelineContext, items);
    
    let code = `// Smart placement for ${items.length === 1 ? items[0].name : `${items.length} clips`}\n`;
    code += `// Strategy: ${strategy}\n\n`;

    switch (strategy) {
      case 'content_match':
        return this._generateContentMatchPlacement(items, analysis, code);
      case 'chronological':
        return this._generateChronologicalPlacement(items, analysis, code);
      case 'energy_flow':
        return this._generateEnergyFlowPlacement(items, analysis, code);
      default:
        return this._generateAutoPlacement(items, analysis, code, options);
    }
  }

  /**
   * Generate code for batch operations
   */
  generateBatchOperationCode(mediaItems, operation, options = {}) {
    const items = Array.isArray(mediaItems) ? mediaItems : [mediaItems];
    
    let code = `// Batch operation: ${operation} on ${items.length} items\n`;
    code += `const batchItems = [\n`;
    
    items.forEach((item, index) => {
      code += `  {\n`;
      code += `    id: '${this._generateClipId(item)}',\n`;
      code += `    name: '${item.name}',\n`;
      code += `    url: '${item.url}',\n`;
      code += `    duration: ${item.duration},\n`;
      code += `    tags: ${JSON.stringify(item.tags || [])}\n`;
      code += `  }${index < items.length - 1 ? ',' : ''}\n`;
    });
    
    code += `];\n\n`;

    switch (operation) {
      case 'addSequentially':
        code += this._generateSequentialAddCode(options);
        break;
      case 'addParallel':
        code += this._generateParallelAddCode(options);
        break;
      case 'createMontage':
        code += this._generateMontageCode(options);
        break;
      case 'arrangeByContent':
        code += this._generateContentArrangementCode(options);
        break;
      default:
        code += `// Unknown batch operation: ${operation}\n`;
    }

    return this._wrapWithMetadata(code, `batch_${operation}`, {
      mediaItems: items,
      operation,
      options
    });
  }

  /**
   * Generate code for drag-and-drop operations
   */
  generateDragDropCode(draggedItems, dropTarget, options = {}) {
    const {
      dropType = 'insert', // 'insert', 'replace', 'append'
      dropPosition = 'before', // 'before', 'after', 'on'
      maintainOrder = true,
      autoArrange = false
    } = options;

    const items = Array.isArray(draggedItems) ? draggedItems : [draggedItems];
    let code = `// Drag & drop: ${items.length} item(s) to ${dropTarget.type || 'timeline'}\n`;

    if (dropTarget.type === 'timeline') {
      return this._generateTimelineDropCode(items, dropTarget, options);
    } else if (dropTarget.type === 'clip') {
      return this._generateClipDropCode(items, dropTarget, options);
    } else if (dropTarget.type === 'track') {
      return this._generateTrackDropCode(items, dropTarget, options);
    }

    return this._wrapWithMetadata(code, 'drag_drop', {
      draggedItems: items,
      dropTarget,
      options
    });
  }

  /**
   * Generate code with context awareness
   */
  generateContextAwareCode(mediaItems, operation, timelineContext, conversationContext = null) {
    // Use smart code generator for context analysis
    const contextAnalysis = smartCodeGenerator.generateContextAwarePrompt(
      `${operation} ${mediaItems.length === 1 ? mediaItems[0].name : `${mediaItems.length} clips`}`,
      timelineContext
    );

    // Generate base code
    let baseCode;
    switch (operation) {
      case 'add':
        baseCode = this.generateAddToTimelineCode(mediaItems);
        break;
      case 'replace':
        baseCode = this.generateReplaceClipCode(mediaItems[0]);
        break;
      case 'insert':
        baseCode = this.generateInsertAtPlayheadCode(mediaItems, timelineContext.currentTime || 0);
        break;
      case 'smart_place':
        baseCode = this.generateSmartPlacementCode(mediaItems, timelineContext);
        break;
      default:
        baseCode = this.generateAddToTimelineCode(mediaItems);
    }

    // Enhance with context awareness
    const enhancedCode = this._enhanceWithContext(baseCode, contextAnalysis, conversationContext);

    return {
      code: enhancedCode.code,
      explanation: enhancedCode.explanation,
      confidence: contextAnalysis.confidence,
      strategy: contextAnalysis.strategy,
      metadata: {
        operation,
        mediaItems: mediaItems.map(item => ({ id: item.id, name: item.name })),
        contextAnalysis: contextAnalysis.contextAnalysis,
        timestamp: Date.now()
      }
    };
  }

  // Private helper methods

  _generateSingleAddCode(item, position, targetTime, targetClipId) {
    let code = `// Add ${item.name} to timeline\n`;
    code += `const clip = {\n`;
    code += `  id: '${this._generateClipId(item)}',\n`;
    code += `  name: '${item.name}',\n`;
    code += `  url: '${item.url}',\n`;
    code += `  duration: ${item.duration},\n`;
    code += `  tags: ${JSON.stringify(item.tags || [])}\n`;
    code += `};\n\n`;

    switch (position) {
      case 'start':
        code += `timeline.addClip(clip, 0);\n`;
        break;
      case 'end':
        code += `timeline.addClip(clip, timeline.getDuration());\n`;
        break;
      case 'playhead':
        code += `timeline.addClip(clip, ${targetTime || 0});\n`;
        break;
      case 'after':
        if (targetClipId) {
          code += `const targetClip = timeline.getClip('${targetClipId}');\n`;
          code += `timeline.addClip(clip, targetClip.startTime + targetClip.duration);\n`;
        }
        break;
      case 'before':
        if (targetClipId) {
          code += `const targetClip = timeline.getClip('${targetClipId}');\n`;
          code += `timeline.addClip(clip, targetClip.startTime);\n`;
          code += `timeline.pushClipsAfterTime(targetClip.startTime, clip.duration);\n`;
        }
        break;
      default:
        code += `timeline.addClip(clip, timeline.getDuration());\n`;
    }

    return this._wrapWithMetadata(code, 'add_single', {
      mediaItem: item,
      position,
      targetTime,
      targetClipId
    });
  }

  _generateMultipleAddCode(items, arrangement, spacing, transitions, position) {
    let code = `// Add ${items.length} clips to timeline\n`;
    code += `const clips = [\n`;
    
    items.forEach((item, index) => {
      code += `  {\n`;
      code += `    id: '${this._generateClipId(item)}',\n`;
      code += `    name: '${item.name}',\n`;
      code += `    url: '${item.url}',\n`;
      code += `    duration: ${item.duration},\n`;
      code += `    tags: ${JSON.stringify(item.tags || [])}\n`;
      code += `  }${index < items.length - 1 ? ',' : ''}\n`;
    });
    
    code += `];\n\n`;

    switch (arrangement) {
      case 'sequence':
        code += this._generateSequenceArrangement(spacing, position);
        break;
      case 'overlay':
        code += this._generateOverlayArrangement(position);
        break;
      case 'parallel':
        code += this._generateParallelArrangement(spacing, position);
        break;
      default:
        code += this._generateSequenceArrangement(spacing, position);
    }

    if (transitions) {
      code += `\n// Add transitions between clips\n`;
      code += `timeline.addTransitionsBetweenClips(clips, { type: 'crossfade', duration: 0.5 });\n`;
    }

    return this._wrapWithMetadata(code, 'add_multiple', {
      mediaItems: items,
      arrangement,
      spacing,
      transitions,
      position
    });
  }

  _generateSequenceArrangement(spacing, position) {
    let code = '';
    
    switch (position) {
      case 'start':
        code += `let currentTime = 0;\n`;
        break;
      case 'end':
        code += `let currentTime = timeline.getDuration();\n`;
        break;
      default:
        code += `let currentTime = timeline.getDuration();\n`;
    }

    code += `clips.forEach(clip => {\n`;
    code += `  timeline.addClip(clip, currentTime);\n`;
    code += `  currentTime += clip.duration + ${spacing};\n`;
    code += `});\n`;

    return code;
  }

  _generateOverlayArrangement(position) {
    let code = '';
    
    switch (position) {
      case 'start':
        code += `const overlayTime = 0;\n`;
        break;
      case 'end':
        code += `const overlayTime = timeline.getDuration();\n`;
        break;
      default:
        code += `const overlayTime = timeline.getDuration();\n`;
    }

    code += `clips.forEach(clip => {\n`;
    code += `  timeline.addClip(clip, overlayTime);\n`;
    code += `});\n`;

    return code;
  }

  _generateParallelArrangement(spacing, position) {
    let code = '';
    
    switch (position) {
      case 'start':
        code += `let trackOffset = 0;\n`;
        break;
      default:
        code += `const baseTime = timeline.getDuration();\n`;
        code += `let trackOffset = 0;\n`;
    }

    code += `clips.forEach((clip, index) => {\n`;
    if (position === 'start') {
      code += `  timeline.addClip(clip, trackOffset, index);\n`;
    } else {
      code += `  timeline.addClip(clip, baseTime + trackOffset, index);\n`;
    }
    code += `  trackOffset += ${spacing};\n`;
    code += `});\n`;

    return code;
  }

  _generateClipId(item) {
    return `clip_${item.id}_${Date.now()}`;
  }

  _wrapWithMetadata(code, operation, metadata) {
    const wrappedCode = `${code}\n// Generated by MediaBrowserCodeGenerator\n// Operation: ${operation}\n// Timestamp: ${Date.now()}`;
    
    // Record in history
    this.codeHistory.push({
      operation,
      code: wrappedCode,
      metadata,
      timestamp: Date.now()
    });

    // Limit history size
    if (this.codeHistory.length > 50) {
      this.codeHistory = this.codeHistory.slice(-50);
    }

    return {
      code: wrappedCode,
      operation,
      metadata,
      timestamp: Date.now()
    };
  }

  _analyzeTimelineForPlacement(timelineContext, mediaItems) {
    return {
      currentClips: timelineContext.clips || [],
      duration: timelineContext.duration || 0,
      hasContent: (timelineContext.clips?.length || 0) > 0,
      contentTypes: this._analyzeContentTypes(timelineContext.clips || []),
      gaps: this._findTimelineGaps(timelineContext.clips || []),
      density: this._calculateTimelineDensity(timelineContext.clips || [], timelineContext.duration || 0),
      mediaAnalysis: this._analyzeMediaItems(mediaItems)
    };
  }

  _analyzeContentTypes(clips) {
    const types = {};
    clips.forEach(clip => {
      clip.tags?.forEach(tag => {
        types[tag] = (types[tag] || 0) + 1;
      });
    });
    return types;
  }

  _findTimelineGaps(clips) {
    const gaps = [];
    const sortedClips = clips.slice().sort((a, b) => a.startTime - b.startTime);
    
    for (let i = 0; i < sortedClips.length - 1; i++) {
      const currentEnd = sortedClips[i].startTime + sortedClips[i].duration;
      const nextStart = sortedClips[i + 1].startTime;
      
      if (nextStart > currentEnd) {
        gaps.push({
          start: currentEnd,
          end: nextStart,
          duration: nextStart - currentEnd
        });
      }
    }
    
    return gaps;
  }

  _calculateTimelineDensity(clips, duration) {
    if (duration === 0) return 0;
    const totalClipDuration = clips.reduce((sum, clip) => sum + clip.duration, 0);
    return totalClipDuration / duration;
  }

  _analyzeMediaItems(items) {
    return {
      totalDuration: items.reduce((sum, item) => sum + item.duration, 0),
      contentTypes: this._analyzeContentTypes(items),
      averageDuration: items.length > 0 ? items.reduce((sum, item) => sum + item.duration, 0) / items.length : 0,
      tags: [...new Set(items.flatMap(item => item.tags || []))]
    };
  }

  _initializeOperationTemplates() {
    // Initialize predefined operation templates
    this.operationTemplates.set('addToEnd', {
      name: 'Add to End',
      description: 'Add clips to the end of the timeline',
      code: 'timeline.addClip(clip, timeline.getDuration());'
    });

    this.operationTemplates.set('replaceClip', {
      name: 'Replace Clip',
      description: 'Replace existing clip with new media',
      code: 'timeline.replaceClip(targetClip.id, newClip);'
    });

    this.operationTemplates.set('insertAtTime', {
      name: 'Insert at Time',
      description: 'Insert clip at specific time position',
      code: 'timeline.addClip(clip, insertTime);'
    });
  }

  _enhanceWithContext(baseCode, contextAnalysis, conversationContext) {
    // Enhance code with context-aware improvements
    let enhancedCode = baseCode.code;
    let explanation = `Generated code to ${baseCode.operation}`;

    // Add context-aware comments
    if (contextAnalysis.contextAnalysis?.userIntent?.complexity === 'advanced') {
      enhancedCode = `// Advanced operation detected\n${enhancedCode}`;
      explanation += " with advanced features";
    }

    // Add safety checks based on context
    if (contextAnalysis.contextAnalysis?.contextComplexity?.level === 'complex') {
      enhancedCode = `// Complex timeline detected - adding safety checks\nif (timeline && timeline.addClip) {\n${enhancedCode.split('\n').map(line => '  ' + line).join('\n')}\n} else {\n  console.error('Timeline not available');\n}`;
    }

    return {
      code: enhancedCode,
      explanation
    };
  }

  // Additional specialized code generators

  _generateContentMatchPlacement(items, analysis, baseCode) {
    let code = baseCode;
    code += `// Content matching placement\n`;
    code += `const timelineContent = ${JSON.stringify(analysis.contentTypes)};\n\n`;

    items.forEach((item, index) => {
      code += `// Find best position for ${item.name} based on content\n`;
      const itemTags = item.tags || [];
      const matchingTags = itemTags.filter(tag => analysis.contentTypes[tag]);
      
      if (matchingTags.length > 0) {
        code += `// Found matching content: ${matchingTags.join(', ')}\n`;
        code += `const matchingClips = timeline.findClipsByTag('${matchingTags[0]}');\n`;
        code += `const insertPosition = matchingClips.length > 0 ? \n`;
        code += `  matchingClips[matchingClips.length - 1].startTime + matchingClips[matchingClips.length - 1].duration : \n`;
        code += `  timeline.getDuration();\n`;
      } else {
        code += `// No matching content found, adding to end\n`;
        code += `const insertPosition = timeline.getDuration();\n`;
      }

      code += `timeline.addClip({\n`;
      code += `  id: '${this._generateClipId(item)}',\n`;
      code += `  name: '${item.name}',\n`;
      code += `  url: '${item.url}',\n`;
      code += `  duration: ${item.duration},\n`;
      code += `  tags: ${JSON.stringify(item.tags || [])}\n`;
      code += `}, insertPosition);\n\n`;
    });

    return this._wrapWithMetadata(code, 'content_match_placement', { items, analysis });
  }

  _generateChronologicalPlacement(items, analysis, baseCode) {
    let code = baseCode;
    code += `// Chronological placement based on content analysis\n`;
    
    // Sort items by potential chronological order (based on tags and content)
    const sortedItems = this._sortItemsChronologically(items);
    
    code += `let currentTime = timeline.getDuration();\n\n`;
    
    sortedItems.forEach((item, index) => {
      code += `// Add ${item.name} in chronological order\n`;
      code += `timeline.addClip({\n`;
      code += `  id: '${this._generateClipId(item)}',\n`;
      code += `  name: '${item.name}',\n`;
      code += `  url: '${item.url}',\n`;
      code += `  duration: ${item.duration},\n`;
      code += `  tags: ${JSON.stringify(item.tags || [])}\n`;
      code += `}, currentTime);\n`;
      code += `currentTime += ${item.duration};\n\n`;
    });

    return this._wrapWithMetadata(code, 'chronological_placement', { items: sortedItems, analysis });
  }

  _generateEnergyFlowPlacement(items, analysis, baseCode) {
    let code = baseCode;
    code += `// Energy flow placement for optimal pacing\n`;
    
    // Sort items by energy level (based on tags and content analysis)
    const energySortedItems = this._sortItemsByEnergy(items);
    
    code += `// Place clips in energy flow order\n`;
    code += `let currentTime = timeline.getDuration();\n\n`;
    
    energySortedItems.forEach((item, index) => {
      code += `// Add ${item.name} (energy level: ${item.energyLevel})\n`;
      code += `timeline.addClip({\n`;
      code += `  id: '${this._generateClipId(item)}',\n`;
      code += `  name: '${item.name}',\n`;
      code += `  url: '${item.url}',\n`;
      code += `  duration: ${item.duration},\n`;
      code += `  tags: ${JSON.stringify(item.tags || [])}\n`;
      code += `}, currentTime);\n`;
      code += `currentTime += ${item.duration};\n\n`;
    });

    return this._wrapWithMetadata(code, 'energy_flow_placement', { items: energySortedItems, analysis });
  }

  _generateAutoPlacement(items, analysis, baseCode, options) {
    // Use the best strategy based on context analysis
    const strategy = this._determineOptimalStrategy(items, analysis, options);
    
    switch (strategy) {
      case 'content_match':
        return this._generateContentMatchPlacement(items, analysis, baseCode);
      case 'chronological':
        return this._generateChronologicalPlacement(items, analysis, baseCode);
      case 'energy_flow':
        return this._generateEnergyFlowPlacement(items, analysis, baseCode);
      default:
        return this._generateSingleAddCode(items[0], 'end');
    }
  }

  _sortItemsChronologically(items) {
    // Sort based on chronological indicators in tags and content
    const chronologicalOrder = ['intro', 'beginning', 'start', 'preparation', 'middle', 'action', 'climax', 'end', 'conclusion', 'result'];
    
    return items.slice().sort((a, b) => {
      const aOrder = this._getChronologicalWeight(a.tags || []);
      const bOrder = this._getChronologicalWeight(b.tags || []);
      return aOrder - bOrder;
    });
  }

  _getChronologicalWeight(tags) {
    const chronologicalOrder = ['intro', 'beginning', 'start', 'preparation', 'middle', 'action', 'climax', 'end', 'conclusion', 'result'];
    
    for (const tag of tags) {
      const index = chronologicalOrder.indexOf(tag);
      if (index !== -1) return index;
    }
    
    return chronologicalOrder.length; // Default to end if no chronological indicators
  }

  _sortItemsByEnergy(items) {
    return items.slice().map(item => ({
      ...item,
      energyLevel: this._calculateEnergyLevel(item)
    })).sort((a, b) => a.energyLevel - b.energyLevel);
  }

  _calculateEnergyLevel(item) {
    const highEnergyTags = ['action', 'fast', 'intense', 'dynamic', 'exciting'];
    const lowEnergyTags = ['slow', 'calm', 'peaceful', 'static', 'talking'];
    
    let energy = 5; // Base energy level
    
    (item.tags || []).forEach(tag => {
      if (highEnergyTags.includes(tag)) energy += 2;
      if (lowEnergyTags.includes(tag)) energy -= 2;
    });
    
    // Adjust based on duration (shorter clips tend to be higher energy)
    if (item.duration < 5) energy += 1;
    if (item.duration > 30) energy -= 1;
    
    return Math.max(1, Math.min(10, energy));
  }

  _determineOptimalStrategy(items, analysis, options) {
    // Determine the best placement strategy based on context
    if (analysis.contentTypes && Object.keys(analysis.contentTypes).length > 0) {
      return 'content_match';
    }
    
    if (items.some(item => this._hasChronologicalIndicators(item.tags || []))) {
      return 'chronological';
    }
    
    if (items.length > 2 && this._hasEnergyVariation(items)) {
      return 'energy_flow';
    }
    
    return 'content_match'; // Default fallback
  }

  _hasChronologicalIndicators(tags) {
    const chronologicalTags = ['intro', 'beginning', 'start', 'preparation', 'middle', 'action', 'climax', 'end', 'conclusion', 'result'];
    return tags.some(tag => chronologicalTags.includes(tag));
  }

  _hasEnergyVariation(items) {
    const energyLevels = items.map(item => this._calculateEnergyLevel(item));
    const minEnergy = Math.min(...energyLevels);
    const maxEnergy = Math.max(...energyLevels);
    return (maxEnergy - minEnergy) > 3; // Significant energy variation
  }

  // Public utility methods

  /**
   * Get code generation history
   */
  getCodeHistory(limit = 10) {
    return this.codeHistory.slice(-limit);
  }

  /**
   * Clear code generation history
   */
  clearHistory() {
    this.codeHistory = [];
  }

  /**
   * Get available operation templates
   */
  getOperationTemplates() {
    return Array.from(this.operationTemplates.entries()).map(([key, template]) => ({
      key,
      ...template
    }));
  }
}

// Export singleton instance
const mediaBrowserCodeGenerator = new MediaBrowserCodeGenerator();
export default mediaBrowserCodeGenerator;